import React, { useCallback, useMemo, useState, useRef } from 'react';
import { EdgeProps } from 'reactflow';
import styled, { keyframes } from 'styled-components';

const flowAnimation = keyframes`
  from { stroke-dashoffset: 20; }
  to { stroke-dashoffset: 0; }
`;

const EdgeContainer = styled.g`
  .react-flow__edge-path {
    stroke: #666;
    stroke-width: 2px;
    fill: none;
  }

  .react-flow__edge-path.selected {
    stroke: #1a73e8;
    stroke-width: 3px;
  }

  .flow-animation-path {
    stroke: #1a73e8;
    stroke-width: 1px;
    fill: none;
    stroke-dasharray: 6 14;
    animation: ${flowAnimation} 0.8s linear infinite;
    pointer-events: none;
  }

  .control-point {
    fill: #1a73e8;
    stroke: #fff;
    stroke-width: 4px;
    r: 10px;
    cursor: move;
    pointer-events: all;
  }

  .control-point:hover {
    stroke: #1557b0;
    stroke-width: 3px;
    r: 10px;
  }

  .control-point.selected {
    stroke: #1a73e8;
    fill: #1a73e8;
  }

  .add-control-point {
    fill: #28a745;
    stroke: #fff;
    stroke-width: 2px;
    r: 6px;
    cursor: pointer;
    opacity: 0;
    pointer-events: all;
  }

  .edge-line:hover .add-control-point,
  .react-flow__edge-path:hover + .add-control-point,
  .react-flow__edge-path.selected ~ .add-control-point {
    opacity: 1;
  }

  .edge-label {
    fill: #333;
    font-size: 12px;
    font-family: Arial, sans-serif;
    text-anchor: middle;
    dominant-baseline: middle;
    pointer-events: all;
    cursor: pointer;
    user-select: none;
  }

  .edge-label-background {
    fill: rgba(255, 255, 255, 0.9);
    stroke: #ccc;
    stroke-width: 1px;
    rx: 3px;
  }
`;

interface HydraulicEdgeProps extends EdgeProps {
  controlPoints?: { x: number; y: number }[];
  onControlPointChange?: (edgeId: string, controlPoints: { x: number; y: number }[]) => void;
  labels?: { text: string; offset: number }[];
  onLabelChange?: (edgeId: string, labels: { text: string; offset: number }[]) => void;
  onAutoEditComplete?: () => void;
  autoEditLabelIndex?: number | null;
  reactFlowInstance?: any;
  simulationFlowRate?: number;
}

function snapToAxis(
  point: { x: number; y: number },
  ref: { x: number; y: number },
  threshold = 5
): { x: number; y: number } {
  const dx = point.x - ref.x;
  const dy = point.y - ref.y;
  const angle = Math.abs(Math.atan2(dy, dx) * 180 / Math.PI);

  if (angle < threshold || angle > 180 - threshold) {
    return { x: point.x, y: ref.y };
  }
  if (Math.abs(angle - 90) < threshold) {
    return { x: ref.x, y: point.y };
  }
  return point;
}

// Generate a path through all points with slightly rounded corners
const CORNER_RADIUS = 8;

function roundedLinePath(points: { x: number; y: number }[]): string {
  if (points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  let path = `M ${points[0].x} ${points[0].y}`;

  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];

    // Vectors from current point to prev and next
    const dxIn = prev.x - curr.x;
    const dyIn = prev.y - curr.y;
    const dxOut = next.x - curr.x;
    const dyOut = next.y - curr.y;

    const lenIn = Math.sqrt(dxIn * dxIn + dyIn * dyIn);
    const lenOut = Math.sqrt(dxOut * dxOut + dyOut * dyOut);

    // Clamp radius so it doesn't exceed half the segment length
    const r = Math.min(CORNER_RADIUS, lenIn / 2, lenOut / 2);

    if (r < 1 || lenIn === 0 || lenOut === 0) {
      // Degenerate — just draw a straight line to the point
      path += ` L ${curr.x} ${curr.y}`;
      continue;
    }

    // Points where the curve starts and ends (offset from corner by radius)
    const startX = curr.x + (dxIn / lenIn) * r;
    const startY = curr.y + (dyIn / lenIn) * r;
    const endX = curr.x + (dxOut / lenOut) * r;
    const endY = curr.y + (dyOut / lenOut) * r;

    // Line to the start of the curve, then quadratic bezier through the corner
    path += ` L ${startX} ${startY} Q ${curr.x} ${curr.y} ${endX} ${endY}`;
  }

  // Final line to the last point
  const last = points[points.length - 1];
  path += ` L ${last.x} ${last.y}`;

  return path;
}

export const HydraulicEdgeComponent: React.FC<HydraulicEdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  selected,
  controlPoints = [],
  onControlPointChange,
  labels = [],
  onLabelChange,
  onAutoEditComplete,
  autoEditLabelIndex,
  reactFlowInstance,
  simulationFlowRate,
  ...edgeProps
}) => {
  const [draggedPointIndex, setDraggedPointIndex] = useState<number | null>(null);
  const [editingLabelIndex, setEditingLabelIndex] = useState<number | null>(null);
  const isDraggingNewPointRef = useRef(false);

  // Auto-enable edit mode for a newly added label
  React.useEffect(() => {
    if (autoEditLabelIndex !== null && autoEditLabelIndex !== undefined) {
      setEditingLabelIndex(autoEditLabelIndex);
    }
  }, [autoEditLabelIndex]);

  const effectiveControlPoints = useMemo(() =>
    controlPoints.length > 0
      ? controlPoints
      : [{ x: (sourceX + targetX) / 2, y: (sourceY + targetY) / 2 }],
    [controlPoints, sourceX, sourceY, targetX, targetY]
  );

  // Full point list including perpendicular stubs at source/target
  const STUB_LENGTH = 10;
  const allPathPointsRef = useRef<{ x: number; y: number }[]>([]);
  const allPathPoints = useMemo(() => {
    const srcDx = sourcePosition === 'left' ? -1 : sourcePosition === 'right' ? 1 : 0;
    const srcDy = sourcePosition === 'top' ? -1 : sourcePosition === 'bottom' ? 1 : 0;
    const tgtDx = targetPosition === 'left' ? -1 : targetPosition === 'right' ? 1 : 0;
    const tgtDy = targetPosition === 'top' ? -1 : targetPosition === 'bottom' ? 1 : 0;

    return [
      { x: sourceX, y: sourceY },
      { x: sourceX + srcDx * STUB_LENGTH, y: sourceY + srcDy * STUB_LENGTH },
      ...effectiveControlPoints,
      { x: targetX + tgtDx * STUB_LENGTH, y: targetY + tgtDy * STUB_LENGTH },
      { x: targetX, y: targetY },
    ];
  }, [sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, effectiveControlPoints]);
  allPathPointsRef.current = allPathPoints;

  // Calculate label/arrow position along the edge based on offset (0–1)
  const getLabelPosition = useCallback((offset: number) => {
    // Find total line length
    let totalLength = 0;
    for (let i = 0; i < allPathPoints.length - 1; i++) {
      const dx = allPathPoints[i + 1].x - allPathPoints[i].x;
      const dy = allPathPoints[i + 1].y - allPathPoints[i].y;
      totalLength += Math.sqrt(dx * dx + dy * dy);
    }

    // Find the point on the line corresponding to the offset
    const targetLength = offset * totalLength;
    let currentLength = 0;

    for (let i = 0; i < allPathPoints.length - 1; i++) {
      const p1 = allPathPoints[i];
      const p2 = allPathPoints[i + 1];
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const segmentLength = Math.sqrt(dx * dx + dy * dy);

      if (currentLength + segmentLength >= targetLength) {
        const remainingLength = targetLength - currentLength;
        const ratio = remainingLength / segmentLength;
        return {
          x: p1.x + dx * ratio,
          y: p1.y + dy * ratio,
          angle: Math.atan2(dy, dx)
        };
      }

      currentLength += segmentLength;
    }

    // Fallback - midpoint
    const dx = targetX - sourceX;
    const dy = targetY - sourceY;
    return { x: (sourceX + targetX) / 2, y: (sourceY + targetY) / 2, angle: Math.atan2(dy, dx) };
  }, [allPathPoints, sourceX, sourceY, targetX, targetY]);

  // Generate SVG path with rounded corners
  const generatePath = useCallback(() => {
    return [roundedLinePath(allPathPoints)];
  }, [allPathPoints]);

  const [edgePath] = generatePath();

  const edgeColor = selected ? '#1a73e8' : '#666';

  // Handle dragging of control points
  const handleControlPointMouseDown = useCallback((index: number, event: React.MouseEvent) => {
    event.stopPropagation();

    setDraggedPointIndex(index);

    const handleMouseMove = (e: MouseEvent) => {
      if (onControlPointChange && reactFlowInstance) {
        const currentPosition = reactFlowInstance.screenToFlowPosition({
          x: e.clientX,
          y: e.clientY,
        });

        let snapped = currentPosition;
        // Snap relative to neighboring points in the full chain (including stubs)
        // allPathPoints = [source, sourceStub, ...controlPoints, targetStub, target]
        // Control point at index maps to chainIndex = index + 2 (after source + sourceStub)
        const pts = allPathPointsRef.current;
        const chainIndex = index + 2;
        if (chainIndex > 0 && chainIndex - 1 < pts.length) {
          snapped = snapToAxis(snapped, pts[chainIndex - 1]);
        }
        if (chainIndex < pts.length - 1) {
          snapped = snapToAxis(snapped, pts[chainIndex + 1]);
        }

        const newControlPoints = [...effectiveControlPoints];
        newControlPoints[index] = { x: snapped.x, y: snapped.y };
        onControlPointChange(id, newControlPoints);
      }
    };

    const handleMouseUp = () => {
      setDraggedPointIndex(null);
      isDraggingNewPointRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [effectiveControlPoints, onControlPointChange, id, reactFlowInstance]);

  // Add a new control point and immediately start dragging it (left-click on edge)
  const handleEdgeMouseDown = useCallback((event: React.MouseEvent) => {
    // Only handle left click
    if (event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();

    if (!onControlPointChange || !reactFlowInstance) return;

    isDraggingNewPointRef.current = true;

    const position = reactFlowInstance.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });
    const clickX = position.x;
    const clickY = position.y;

    // Use allPathPoints (with stubs) for hit-testing which segment was clicked
    // allPathPoints = [source, sourceStub, ...controlPoints, targetStub, target]
    // We only insert new control points between the stub-bounded segments (indices 1..len-2)
    let bestIndex = 0;
    let bestPoint = { x: clickX, y: clickY };
    let bestDistance = Infinity;

    // Search segments between sourceStub and targetStub for closest hit
    const pts = allPathPointsRef.current;
    for (let i = 1; i < pts.length - 2; i++) {
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const length = Math.sqrt(dx * dx + dy * dy);
      if (length === 0) continue;

      const dx_click = clickX - p1.x;
      const dy_click = clickY - p1.y;
      const t = Math.max(0, Math.min(1, (dx_click * dx + dy_click * dy) / (length * length)));
      const closestX = p1.x + t * dx;
      const closestY = p1.y + t * dy;
      const distance = Math.sqrt((clickX - closestX) ** 2 + (clickY - closestY) ** 2);

      if (distance < bestDistance) {
        bestDistance = distance;
        // Map back to control point index: allPathPoints index i corresponds
        // to effectiveControlPoints index (i - 2) for the segment start,
        // so inserting before control point (i - 1)
        bestIndex = i - 1;
        bestPoint = { x: closestX, y: closestY };
      }
    }

    // Clamp bestIndex within valid range for effectiveControlPoints
    bestIndex = Math.max(0, Math.min(bestIndex, effectiveControlPoints.length));

    const newControlPoints = [...effectiveControlPoints];
    newControlPoints.splice(bestIndex, 0, bestPoint);
    onControlPointChange(id, newControlPoints);

    // Immediately start dragging the newly inserted point
    setDraggedPointIndex(bestIndex);

    const handleMouseMove = (e: MouseEvent) => {
      if (onControlPointChange && reactFlowInstance) {
        const currentPosition = reactFlowInstance.screenToFlowPosition({
          x: e.clientX,
          y: e.clientY,
        });
        // Snap relative to neighboring points in the full chain (including stubs)
        // Use ref to get latest allPathPoints during drag
        const pts = allPathPointsRef.current;
        let snapped = currentPosition;
        const chainIndex = bestIndex + 2;
        if (chainIndex > 0 && chainIndex - 1 < pts.length) {
          const prevPt = bestIndex === 0
            ? pts[1] // sourceStub
            : newControlPoints[bestIndex - 1];
          snapped = snapToAxis(snapped, prevPt);
        }
        if (chainIndex < pts.length - 1) {
          const nextPt = bestIndex === newControlPoints.length - 1
            ? pts[pts.length - 2] // targetStub
            : newControlPoints[bestIndex + 1];
          snapped = snapToAxis(snapped, nextPt);
        }

        const updatedPoints = [...newControlPoints];
        updatedPoints[bestIndex] = { x: snapped.x, y: snapped.y };
        onControlPointChange(id, updatedPoints);
      }
    };

    const handleMouseUp = () => {
      setDraggedPointIndex(null);
      isDraggingNewPointRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [effectiveControlPoints, onControlPointChange, id, reactFlowInstance]);

  // Remove control point (right-click)
  const handleControlPointRightClick = useCallback((index: number, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (onControlPointChange && effectiveControlPoints.length > 1) {
      const newControlPoints = effectiveControlPoints.filter((_, i) => i !== index);
      onControlPointChange(id, newControlPoints);
    }
  }, [effectiveControlPoints, onControlPointChange, id]);

  // Mid-path arrow at 50%
  const midArrow = useMemo(() => {
    const pos = getLabelPosition(0.5);
    const angle = pos.angle * (180 / Math.PI);
    // Small triangle pointing in the flow direction
    return { x: pos.x, y: pos.y, angle };
  }, [getLabelPosition]);

  return (
    <EdgeContainer>
      <defs>
        {/* Custom end arrow marker */}
        <marker
          id={`arrow-${id}`}
          markerWidth="8"
          markerHeight="6"
          refX="7"
          refY="3"
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <polygon points="0 0, 8 3, 0 6" fill={edgeColor} />
        </marker>
      </defs>

      {/* Wide invisible hit area — always present for easy selection */}
      <path
        d={edgePath}
        stroke="transparent"
        strokeWidth="20"
        fill="none"
        style={{ cursor: selected ? 'crosshair' : 'pointer' }}
        onMouseDown={selected ? handleEdgeMouseDown : undefined}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />

      {/* Main edge path — explicit fill="none" prevents black fill in image export */}
      <path
        id={id}
        style={style}
        className={`react-flow__edge-path ${selected ? 'selected' : ''} edge-line`}
        d={edgePath}
        fill="none"
        markerEnd={`url(#arrow-${id})`}
      />

      {/* Animated flow dashes (only when simulation results are active) */}
      {simulationFlowRate !== undefined && (
        <path
          d={edgePath}
          className="flow-animation-path"
        />
      )}

      {/* Mid-path direction arrow */}
      <polygon
        points="-4,-3 4,0 -4,3"
        fill={edgeColor}
        transform={`translate(${midArrow.x}, ${midArrow.y}) rotate(${midArrow.angle})`}
        style={{ pointerEvents: 'none' }}
      />

      {/* Control points - visible for selected edges */}
      {selected && effectiveControlPoints.map((point, index) => (
        <circle
          key={`control-${index}`}
          cx={point.x}
          cy={point.y}
          className={`control-point ${draggedPointIndex === index ? 'selected' : ''}`}
          onMouseDown={(e) => handleControlPointMouseDown(index, e)}
          onContextMenu={(e) => handleControlPointRightClick(index, e)}
        />
      ))}

      {/* Flow rate badge from simulation */}
      {simulationFlowRate !== undefined && (() => {
        const midPos = getLabelPosition(0.5);
        return (
          <g>
            <rect
              x={midPos.x - 28}
              y={midPos.y - 18}
              width="56"
              height="14"
              rx="7"
              fill="#e8f0fe"
              stroke="#1a73e8"
              strokeWidth="0.5"
            />
            <text
              x={midPos.x}
              y={midPos.y - 11}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#1967d2"
              fontSize="9"
              fontWeight="600"
              fontFamily="Arial, sans-serif"
              style={{ pointerEvents: 'none' }}
            >
              {simulationFlowRate.toFixed(1)} l/min
            </text>
          </g>
        );
      })()}

      {/* Labels on the edge */}
      {labels.map((label, index) => {
        const position = getLabelPosition(label.offset);
        return (
          <g key={`label-${index}`}>
            {editingLabelIndex === index ? (
              // Edit mode - input field
              <foreignObject
                x={position.x - 50}
                y={position.y - 10}
                width="100"
                height="20"
              >
                <input
                  type="text"
                  defaultValue={label.text}
                  autoFocus
                  onBlur={(e) => {
                    const newText = e.target.value.trim();
                    if (newText === "") {
                      const newLabels = labels.filter((_, i) => i !== index);
                      onLabelChange?.(id, newLabels);
                    } else {
                      const newLabels = [...labels];
                      newLabels[index] = { ...label, text: newText };
                      onLabelChange?.(id, newLabels);
                    }
                    setEditingLabelIndex(null);
                    onAutoEditComplete?.();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    } else if (e.key === 'Escape') {
                      setEditingLabelIndex(null);
                      onAutoEditComplete?.();
                    }
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: '1px solid #ccc',
                    borderRadius: '3px',
                    padding: '0 4px',
                    fontSize: '12px',
                    fontFamily: 'Arial, sans-serif',
                    textAlign: 'center',
                    background: 'white',
                    outline: 'none'
                  }}
                />
              </foreignObject>
            ) : (
              // Display mode
              <>
                <rect
                  x={position.x - 40}
                  y={position.y - 8}
                  width="80"
                  height="16"
                  className="edge-label-background"
                />
                <text
                  x={position.x}
                  y={position.y}
                  className="edge-label"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingLabelIndex(index);
                  }}
                >
                  {label.text}
                </text>
              </>
            )}
          </g>
        );
      })}
    </EdgeContainer>
  );
};
